import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Bell, BellOff, Loader2 } from "lucide-react";
import {
  isPushSupported,
  notificationPermission,
  getPushStatus,
  requestPermissionAndSubscribe,
  unsubscribe,
  sendTestPush,
} from "@/lib/push";

/**
 * PushDeliveryRow — dark-card-friendly delivery toggle for Web Push.
 *
 * Behaviour:
 *   • Detects browser support + permission state.
 *   • If unsupported/denied → renders an informational (disabled) row.
 *   • If not yet subscribed → the "Enable" button prompts permission + subscribes.
 *   • Once subscribed → the switch acts as a delivery preference toggle
 *     (the underlying browser subscription stays alive so the user can turn
 *     push back on without re-granting permission). "Send test" fires a
 *     server-side push.
 *
 * Props:
 *   checked          — current recipe-side "push delivery" preference.
 *   onChange(v)      — persist the preference (parent handles the PATCH).
 *   testid           — root data-testid.
 */
export default function PushDeliveryRow({ checked, onChange, testid = "delivery-push" }) {
  const supported = isPushSupported();
  const [perm, setPerm] = useState(supported ? notificationPermission() : "unsupported");
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!supported) return;
      try {
        const s = await getPushStatus();
        if (mounted) setSubscribed(!!s?.subscribed);
      } catch {
        // 401 during logout, ignore — user isn't looking at this anyway.
      }
    })();
    return () => { mounted = false; };
  }, [supported]);

  const enable = async () => {
    setBusy(true);
    const res = await requestPermissionAndSubscribe();
    setBusy(false);
    setPerm(notificationPermission());
    if (res.ok) {
      setSubscribed(true);
      onChange?.(true);
      toast.success("Notifications enabled — try 'Send test'.");
    } else if (res.reason === "denied") {
      toast.error("Notifications blocked in your browser settings.");
    } else if (res.reason === "unsupported") {
      toast.error("This browser doesn't support push yet.");
    } else {
      toast.error("Couldn't enable notifications. Try again.");
    }
  };

  const disable = async () => {
    setBusy(true);
    await unsubscribe();
    setBusy(false);
    setSubscribed(false);
    onChange?.(false);
    toast.success("Notifications turned off.");
  };

  const runTest = async () => {
    setTesting(true);
    try {
      await sendTestPush();
      toast.success("Test push sent — check your device.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test push failed.");
    } finally {
      setTesting(false);
    }
  };

  const denied = perm === "denied";

  return (
    <div
      className="flex items-start justify-between gap-4"
      data-testid={testid}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-sm text-white">
          <Bell size={13} className="text-[var(--rammy-cyan)]"/>
          Push notifications
          <span className="text-[9px] uppercase tracking-widest bg-[var(--rammy-cyan)]/20 text-[var(--rammy-cyan)] px-1.5 py-0.5 rounded-full">
            Live
          </span>
        </div>
        <div className="text-[12px] text-white/50 mt-1 leading-relaxed">
          {!supported
            ? "Your browser doesn't support push. On iPhone, install Rammy to your home screen first."
            : denied
              ? "You blocked notifications for Rammy. Re-enable them in your browser site settings."
              : subscribed
                ? "Delivered as a native notification even when Rammy is closed."
                : "Get briefings on your phone or laptop — even when Rammy is closed."}
        </div>

        {subscribed && checked && (
          <button
            onClick={runTest}
            disabled={testing}
            data-testid={`${testid}-send-test`}
            className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-[var(--rammy-cyan)] hover:text-[var(--rammy-cyan-dark)] transition disabled:opacity-60"
          >
            {testing ? <Loader2 size={11} className="animate-spin"/> : <Bell size={11}/>}
            Send a test push
          </button>
        )}
      </div>

      <div className="shrink-0 flex items-center gap-2">
        {!supported || denied ? (
          <span
            className="inline-flex items-center gap-1.5 text-[11px] text-white/40"
            data-testid={`${testid}-disabled`}
          >
            <BellOff size={12}/> Unavailable
          </span>
        ) : !subscribed ? (
          <button
            onClick={enable}
            disabled={busy}
            data-testid={`${testid}-enable`}
            className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-[12px] bg-[var(--rammy-cyan)] text-[var(--rammy-text)] hover:bg-[var(--rammy-cyan)]/90 transition disabled:opacity-60"
          >
            {busy ? <Loader2 size={12} className="animate-spin"/> : <Bell size={12}/>}
            Enable
          </button>
        ) : (
          <Switch
            checked={!!checked}
            onCheckedChange={(v) => {
              onChange?.(v);
              // If the user flips this off, offer to fully unsubscribe.
              // We keep it lightweight: pref off = don't send, subscription stays valid.
              if (!v) {
                // no-op; subscription remains, prefs handle gating.
              }
            }}
            data-testid={`${testid}-switch`}
          />
        )}
        {subscribed && !busy && (
          <button
            onClick={disable}
            data-testid={`${testid}-disable`}
            className="text-[11px] text-white/40 hover:text-white/70 transition"
            title="Remove this device"
          >
            Forget
          </button>
        )}
      </div>
    </div>
  );
}
